// Kalender (Plantafel) für die Seite Termine – A-027 S1.
// Woche / Monat / je Monteur am PC, Tag am Handy. Ziehen = verschieben, unten ziehen = verlängern (Maus und Finger).
// Gespeichert wird über onAendern (die vorhandene PUT-Route der Seite). Klick in freie Zeit / auf Termin öffnet
// die Seitenleiste rechts im Kalender (onNeu / onOeffnen) – es gibt keine zweite Eingabemaske und kein überlagerndes Fenster.
// Mit der Maus: über freie Zeit ziehen = Start und Ende (wie Google Kalender). „auswahl“ = Planung läuft (Seitenleiste „neu“ offen):
// Mitarbeiter-Filter, Urlaub sperrt die Auswahl, Termine bleiben trotzdem verschiebbar.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { format, getISOWeek } from "date-fns";
import { de } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, AlertTriangle, CheckCircle2, Clock, XCircle, Loader2, ListChecks, Square } from "lucide-react";
import { useArbeitszeit } from "./arbeitszeit";
import { colorForUser, initialsOf } from "@/lib/avatarUtils";
import {
  RASTER_MIN, abwesendAmTag, ausschnittAmTag, findeKonflikte, alleKonflikte, gleicherTag, konfliktText, leseZeit,
  leseTag, monatsTage, monteureVon, plusMinuten, plusTage, runden, schreibeZeit, spurenVergeben, stundenBereich,
  tagAnfang, tagSchluessel, terminZeiten, wochenStart,
} from "./kalenderLogik";

const HOUR_H = 48;                       // Pixel je Stunde (30 Minuten = 24 px)
const px = (min) => (min / 60) * HOUR_H;
const OHNE = "__ohne__";
const GRAU = "#94a3b8";
const ANSICHT_KEY = "termine_kalender_ansicht";
const ZEIGE_KEY = "termine_kalender_zeige";
const gemerktesZeige = () => {
  try { const a = localStorage.getItem(ZEIGE_KEY); return ["termine", "aufgaben", "beides"].includes(a) ? a : "beides"; }
  catch { return "beides"; }
};
const AUFGABE_FARBE = "#7c3aed";
const STATUS_TEXT = {
  wartet_auf_go: "Wartet auf GO", bestaetigt: "Bestätigt", im_kalender: "Im Kalender", erledigt: "Erledigt", abgesagt: "Abgesagt",
};
const hhmm = (d) => format(d, "HH:mm");

const farbeVon = (user) => colorForUser(user)?.hex || GRAU;

const merkeAnsicht = (a) => { try { localStorage.setItem(ANSICHT_KEY, a); } catch { /* ohne Speicher weiter */ } };
const gemerkteAnsicht = () => {
  try { const a = localStorage.getItem(ANSICHT_KEY); return ["woche", "monat", "monteure"].includes(a) ? a : "woche"; }
  catch { return "woche"; }
};

export default function TerminKalender({
  termine, laden, kunden, projekte, benutzer, abwesenheiten = [],
  legende = [], gewaehlt = new Set(), onMonteurUmschalten,
  onOeffnen, onNeu, onAendern, aufgaben = [], onOeffnenAufgabe,
  auswahl = false, startDatum = null,   // Planung läuft: Woche/Tag/je Mitarbeiter, Termine UND Aufgaben, Urlaub der gewählten Mitarbeiter sperrt
  markierung = null,                    // Planung: Vorschlag „Nächste freie Zeit“ { start, ende } (Klick = übernehmen)
  entwurf = null,                       // Planung: Zeit des Entwurfs in der Seitenleiste { start, ende, titel } (gestrichelter Kasten)
  onAufgabeErledigt = null,             // Haken an der Aufgabe oben am Tag
  onDatum = null, springeZu = null,     // Auswahl-Modus: angezeigtes Datum melden / zu einem Datum springen ({ datum, n })
  rasterAbzug = 250,                    // Platz über dem Raster (für die Höhe)
}) {
  const arbeitszeit = useArbeitszeit();   // Arbeitszeit des Betriebs (Einstellungen)
  // Filter „Termine / Aufgaben / beides“ (gemerkt)
  const [zeigeGemerkt, setZeige] = useState(gemerktesZeige);
  const zeige = auswahl ? "beides" : zeigeGemerkt;   // bei der Planung immer Termine UND Aufgaben
  const waehleZeige = (z) => { setZeige(z); try { localStorage.setItem(ZEIGE_KEY, z); } catch { /* ohne Speicher weiter */ } };
  const [mobil, setMobil] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(max-width: 640px)").matches : false));
  const [pcAnsicht, setPcAnsicht] = useState(() => (auswahl ? "woche" : gemerkteAnsicht()));
  const [datum, setDatum] = useState(() => tagAnfang(startDatum || new Date()));
  const [jetzt, setJetzt] = useState(() => new Date());
  const [vorlaeufig, setVorlaeufig] = useState({});   // id -> {start, ende, monteure} bis die Speicherung durch ist
  const [vorschau, setVorschau] = useState(null);     // Geisterbild beim Ziehen
  const scroller = useRef(null);
  const [rasterBreite, setRasterBreite] = useState(900);
  const [kopfHoehe, setKopfHoehe] = useState(44);   // Höhe der festen Kopfzeile im Raster (für den Kartentext)
  const zieh = useRef(null);
  const vorschauRef = useRef(null);
  const klickSperre = useRef(false);

  const ansicht = auswahl ? (pcAnsicht === "tag" ? "tag" : (pcAnsicht === "monteure" && !mobil ? "monteure" : "woche")) : (mobil ? "tag" : pcAnsicht);   // Auswahl: Woche ist Standard (auch am Handy), je Mitarbeiter nur am PC

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const f = () => setMobil(mq.matches);
    mq.addEventListener?.("change", f);
    return () => mq.removeEventListener?.("change", f);
  }, []);
  useEffect(() => { const i = setInterval(() => setJetzt(new Date()), 30000); return () => clearInterval(i); }, []);

  // Breite des Rasters merken: schmale Spalten bekommen kürzere Karten-Texte
  useEffect(() => {
    const el = scroller.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const messen = () => {
      setRasterBreite(el.clientWidth);
      const k = el.querySelector("[data-kopf]");
      if (k) setKopfHoehe(Math.round(k.getBoundingClientRect().height));
    };
    const ro = new ResizeObserver(messen);
    ro.observe(el);
    messen();
    return () => ro.disconnect();
  }, [ansicht]);

  useEffect(() => { if (auswahl) onDatum?.(datum); }, [datum, auswahl]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (springeZu?.datum) setDatum(tagAnfang(springeZu.datum)); }, [springeZu?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  // Vorschlag „Nächste freie Zeit“: so scrollen, dass die markierte Zeit zu sehen ist
  useEffect(() => {
    if (!auswahl || !markierung) return undefined;
    const t = setTimeout(() => {
      const el = scroller.current;
      if (el) el.scrollTop = Math.max(0, px(markierung.start.getHours() * 60 + markierung.start.getMinutes() - bereich.von * 60) - 60);
    }, 60);
    return () => clearTimeout(t);
  }, [markierung]); // eslint-disable-line react-hooks/exhaustive-deps

  const waehleAnsicht = (a) => { setPcAnsicht(a); if (!auswahl) merkeAnsicht(a); };

  const nameVon = useCallback((u) => benutzer.find((b) => b.username === u)?.anzeige_name || u, [benutzer]);
  const kundeKurz = useCallback((t) => {
    const k = kunden.find((x) => x.id === t.kunde_id);
    if (!k) return "";
    return k.firma || [k.vorname, k.nachname].filter(Boolean).join(" ") || k.name || "";
  }, [kunden]);

  // Termine mit vorläufiger Verschiebung (sofort sichtbar, bevor der Server geantwortet hat)
  const eff = useMemo(() => (zeige === "aufgaben" ? [] : termine).map((t) => {
    const v = vorlaeufig[t.id];
    return v ? { ...t, start: schreibeZeit(v.start), ende: schreibeZeit(v.ende), monteur_usernames: v.monteure, monteur_username: v.monteure[0] || "" } : t;
  }), [termine, vorlaeufig, zeige]);
  const konflikte = useMemo(() => alleKonflikte(eff), [eff]);

  // Aufgaben mit Fälligkeit: farbiger Streifen am Fälligkeitstag (offene und laufende; bei gewähltem Monteur nur seine)
  const aufgabenAm = useCallback((tag, monteur) => {
    if (zeige === "termine") return [];
    const key = tagSchluessel(tag);
    return aufgaben.filter((a) => (a.faellig_am || "").slice(0, 10) === key && a.status !== "erledigt"
      && (monteur === undefined || monteur === null ? (gewaehlt.size ? gewaehlt.has(a.zugewiesen_an) : true)
        : (monteur === OHNE ? !a.zugewiesen_an : a.zugewiesen_an === monteur)));
  }, [aufgaben, zeige, gewaehlt]);
  const aufgabenStreifen = (a, kurz) => (
    <div key={a.id} data-aufgabe data-testid={`kal-aufgabe-zeile-${a.id}`} className={`w-full flex items-center rounded-[4px] text-[11px] leading-4 text-white ${kurz ? "" : "mt-0.5"}`} style={{ background: AUFGABE_FARBE }}>
      {onAufgabeErledigt && (
        <button type="button" data-testid={`kal-aufgabe-haken-${a.id}`} aria-label={`Aufgabe „${a.titel}“ erledigt`} title="Erledigt (Haken setzen)"
          onClick={(ev) => { ev.stopPropagation(); onAufgabeErledigt(a); }}
          className="shrink-0 w-5 h-5 flex items-center justify-center hover:bg-white/20 rounded-[4px]"><Square className="w-3 h-3" /></button>
      )}
      <button type="button" data-testid={`kal-aufgabe-${a.id}`}
        onClick={(ev) => { ev.stopPropagation(); onOeffnenAufgabe?.(a); }}
        title={`Aufgabe: ${a.titel}${a.zugewiesen_an ? ` (${nameVon(a.zugewiesen_an)})` : ""}`}
        className={`min-w-0 flex-1 flex items-center gap-1 py-[1px] pr-1 text-left ${onAufgabeErledigt ? "" : "pl-1"}`}>
        {!onAufgabeErledigt && <ListChecks className="w-3 h-3 shrink-0" />}<span className="truncate">{a.titel}</span>
      </button>
    </div>
  );

  // ---------- sichtbare Tage ----------
  const tage = useMemo(() => {
    if (ansicht === "woche") { const s = wochenStart(datum); return Array.from({ length: 7 }, (_, i) => plusTage(s, i)); }
    if (ansicht === "monat") return monatsTage(datum);
    return [tagAnfang(datum)];
  }, [ansicht, datum]);

  // Entwurf liegt in einer Woche/an einem Tag, die gerade nicht zu sehen sind (Klick im Monat, „Nächste freie Zeit“): dorthin springen
  const entwurfStart = entwurf ? entwurf.start.getTime() : null;
  useEffect(() => {
    if (entwurfStart === null) return;
    const d = new Date(entwurfStart);
    if (!tage.some((t) => gleicherTag(t, d))) setDatum(tagAnfang(d));
  }, [entwurfStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const weiter = (richtung) => {
    setDatum((d) => {
      if (ansicht === "woche") return plusTage(d, 7 * richtung);
      if (ansicht === "monat") return new Date(d.getFullYear(), d.getMonth() + richtung, 1);
      return plusTage(d, richtung);
    });
  };

  const titelText = useMemo(() => {
    if (ansicht === "woche") {
      const a = tage[0], b = tage[6];
      return `KW ${getISOWeek(a)} · ${format(a, "d. MMM", { locale: de })} – ${format(b, "d. MMM yyyy", { locale: de })}`;
    }
    if (ansicht === "monat") return format(datum, "LLLL yyyy", { locale: de });
    return format(datum, mobil ? "EEE, d. MMM yyyy" : "EEEE, d. MMMM yyyy", { locale: de });
  }, [ansicht, tage, datum, mobil]);

  // ---------- Spalten der Zeitraster-Ansichten ----------
  const spalten = useMemo(() => {
    if (ansicht === "monat") return [];
    if (ansicht !== "monteure") return tage.map((t) => ({ key: tagSchluessel(t), tag: t, monteur: null }));
    const tag = tage[0];
    const amTag = eff.filter((t) => { const z = terminZeiten(t); return z && ausschnittAmTag(z, tag); });
    let namen = [...new Set([...benutzer.map((b) => b.username), ...amTag.flatMap(monteureVon)])];
    if (gewaehlt.size) namen = namen.filter((n) => gewaehlt.has(n));
    const abw = new Set(abwesenheiten.filter((a) => abwesendAmTag(a, tag)).map((a) => a.username));
    const aktive = new Set([...amTag.flatMap(monteureVon), ...abw]);
    if (namen.length > 8) namen = namen.filter((n) => aktive.has(n));
    const sp = namen.map((n) => ({ key: `${tagSchluessel(tag)}|${n}`, tag, monteur: n }));
    return auswahl ? sp : [...sp, { key: `${tagSchluessel(tag)}|${OHNE}`, tag, monteur: OHNE }];
  }, [ansicht, tage, eff, benutzer, abwesenheiten, gewaehlt, auswahl]);

  // Ausschnitte je Spalte
  const eintraegeJeSpalte = useMemo(() => {
    const karte = {};
    for (const sp of spalten) {
      const liste = [];
      for (const t of eff) {
        const z = terminZeiten(t);
        if (!z) continue;
        if (sp.monteur) {
          const m = monteureVon(t);
          if (sp.monteur === OHNE ? m.length > 0 : !m.includes(sp.monteur)) continue;
        }
        const a = ausschnittAmTag(z, sp.tag);
        if (!a) continue;
        liste.push({ t, z, ...a, farbe: farbeVon(sp.monteur && sp.monteur !== OHNE ? sp.monteur : monteureVon(t)[0]) });
      }
      karte[sp.key] = spurenVergeben(liste);
    }
    return karte;
  }, [spalten, eff]);

  const bereich = useMemo(() => stundenBereich([...Object.values(eintraegeJeSpalte).flat(), { von: arbeitszeit.vonMin, bis: arbeitszeit.bisMin }]), [eintraegeJeSpalte, arbeitszeit]);   // 6–20 Uhr, wächst mit Terminen und Arbeitszeit
  const hoehe = (bereich.bis - bereich.von) * HOUR_H;
  const spaltenBreite = Math.max((rasterBreite - (mobil ? 38 : 50)) / Math.max(spalten.length, 1), ansicht === "monteure" ? 150 : mobil ? 0 : 118);

  // beim Öffnen/Wechseln zur Arbeitszeit bzw. zur Jetzt-Linie scrollen
  // Beim Öffnen / Tag- oder Ansichtswechsel: so scrollen, dass der erste Termin der Ansicht oben sichtbar ist (sonst 7 Uhr)
  const datumKey = tagSchluessel(datum);
  const ersterTermin = useMemo(() => {
    const alle = Object.values(eintraegeJeSpalte).flat();
    return alle.length ? Math.min(...alle.map((a) => a.von)) : null;
  }, [eintraegeJeSpalte]);
  const gescrollt = useRef("");
  useEffect(() => {
    const el = scroller.current;
    if (!el || ansicht === "monat" || laden) return;
    const schluessel = `${ansicht}|${datumKey}|${arbeitszeit.vonMin}`;
    if (gescrollt.current === schluessel) return;
    gescrollt.current = schluessel;
    const min = Math.max(bereich.von * 60, ersterTermin === null ? arbeitszeit.vonMin : ersterTermin);
    el.scrollTop = Math.max(0, px(min - bereich.von * 60) - 6);
  }, [ansicht, datumKey, bereich.von, ersterTermin, laden, arbeitszeit.vonMin]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Abwesenheit ----------
  const abwesendeAm = useCallback((tag) => abwesenheiten.filter((a) => abwesendAmTag(a, tag)), [abwesenheiten]);

  // ---------- Ziehen ----------
  const abbrechen = useCallback(() => {
    const z = zieh.current;
    if (z?.timer) clearTimeout(z.timer);
    if (z?.sperre) window.removeEventListener("touchmove", z.sperre);
    zieh.current = null;
    vorschauRef.current = null;
    setVorschau(null);
  }, []);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape" && zieh.current?.aktiv) abbrechen(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [abbrechen]);

  const berechneVorschau = (z, e) => {
    const stapel = document.elementsFromPoint(e.clientX, e.clientY);
    const ziel = stapel.find((el) => el.dataset && (el.dataset.spalteTag || el.dataset.monatTag));
    const zielTagTxt = ziel?.dataset.spalteTag || ziel?.dataset.monatTag || z.quellTag;
    const zielMonteur = ziel?.dataset.spalteTag ? (ziel.dataset.spalteMonteur ?? null) : null;
    const tageDiff = Math.round((leseTag(zielTagTxt) - leseTag(z.quellTag)) / 86400000);
    const dMin = z.art === "ziehen-zeit" || z.art === "dehnen" ? runden(((e.clientY - z.y0) / HOUR_H) * 60) : 0;
    let start = z.z.start, ende = z.z.ende, monteure = z.monteure;
    if (z.art === "dehnen") {
      ende = plusMinuten(z.z.ende, dMin);
      if (ende.getTime() < plusMinuten(start, RASTER_MIN).getTime()) ende = plusMinuten(start, RASTER_MIN);
    } else {
      const dauer = z.z.ende.getTime() - z.z.start.getTime();
      start = plusMinuten(plusTage(z.z.start, tageDiff), dMin);
      ende = new Date(start.getTime() + dauer);
      if (z.quellMonteur !== null && zielMonteur !== null && zielMonteur !== z.quellMonteur) {
        const ohneQuelle = z.quellMonteur === OHNE ? monteure : monteure.filter((m) => m !== z.quellMonteur);
        monteure = zielMonteur === OHNE ? ohneQuelle : [...new Set([zielMonteur, ...ohneQuelle])];
        // erster Eintrag bleibt der gewählte Monteur der Zielspalte
      }
    }
    const spalteKey = ziel?.dataset.spalteTag ? ziel.dataset.spalteKey : ziel?.dataset.monatTag ? `monat|${zielTagTxt}` : null;
    return { id: z.id, start, ende, monteure, spalteKey, art: z.art };
  };

  const fertig = async (v) => {
    const z = zieh.current;
    const t = z?.termin;
    abbrechen();
    if (!v || !t) return;
    const alt = terminZeiten(t);
    const altMonteure = monteureVon(t);
    const gleichZeit = v.start.getTime() === alt.start.getTime() && v.ende.getTime() === alt.ende.getTime();
    const gleichMonteure = v.monteure.join("|") === altMonteure.join("|");
    if (gleichZeit && gleichMonteure) return;
    const patch = { start: schreibeZeit(v.start), ende: schreibeZeit(v.ende) };
    if (!gleichMonteure) { patch.monteur_usernames = v.monteure; patch.monteur_username = v.monteure[0] || ""; }
    setVorlaeufig((o) => ({ ...o, [t.id]: { start: v.start, ende: v.ende, monteure: v.monteure } }));
    try {
      await onAendern(t, patch);
      if (t.status !== "abgesagt") {
        const k = findeKonflikte({ id: t.id, start: v.start, ende: v.ende, monteure: v.monteure }, eff);
        if (k.length) toast.warning(`Achtung, Doppelbelegung: ${konfliktText(k, nameVon)}`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(`${err?.response?.data?.detail || "Speichern fehlgeschlagen"} – der Termin steht wieder an seinem alten Platz.`);
    } finally {
      setVorlaeufig((o) => { const n = { ...o }; delete n[t.id]; return n; });
    }
  };

  const beginneZiehen = (e, t, z, quellTag, quellMonteur, art) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    abbrechen();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* egal */ }
    const touch = e.pointerType === "touch";
    const s = {
      id: t.id, termin: t, z, art, touch, x0: e.clientX, y0: e.clientY, aktiv: false, timer: null, sperre: null,
      quellTag, quellMonteur, monteure: monteureVon(t),
    };
    zieh.current = s;
    const starte = () => {
      if (zieh.current !== s) return;
      s.aktiv = true;
      if (touch) { s.sperre = (ev) => { if (ev.cancelable) ev.preventDefault(); }; window.addEventListener("touchmove", s.sperre, { passive: false }); try { navigator.vibrate?.(15); } catch { /* egal */ } }
      const v = { id: t.id, start: z.start, ende: z.ende, monteure: s.monteure, spalteKey: null, art };
      vorschauRef.current = v; setVorschau(v);
    };
    if (touch && art !== "dehnen") s.timer = setTimeout(starte, 380);
    else if (touch) starte();
  };

  const bewegen = (e) => {
    const z = zieh.current;
    if (!z) return;
    const weg = Math.hypot(e.clientX - z.x0, e.clientY - z.y0);
    if (!z.aktiv) {
      if (z.touch && z.art !== "dehnen") { if (weg > 8) abbrechen(); return; }  // Finger wischt = Seite scrollt
      if (weg < 5) return;
      z.aktiv = true;
    }
    const v = berechneVorschau(z, e);
    vorschauRef.current = v; setVorschau(v);
  };

  const loslassen = () => {
    const z = zieh.current;
    if (!z) return;
    if (z.timer) clearTimeout(z.timer);
    if (z.aktiv) { klickSperre.current = true; setTimeout(() => { klickSperre.current = false; }, 400); fertig(vorschauRef.current); } else abbrechen();
  };

  // Wischen im Tagesblick (Finger auf freier Fläche)
  const wisch = useRef(null);
  const wischStart = (e) => { if (mobil && e.pointerType === "touch" && !e.target.closest("[data-karte]")) wisch.current = { x: e.clientX, y: e.clientY }; };
  const wischEnde = (e) => {
    const w = wisch.current; wisch.current = null;
    if (!w) return;
    const dx = e.clientX - w.x, dy = e.clientY - w.y;
    if (Math.abs(dx) > 60 && Math.abs(dy) < 45) weiter(dx < 0 ? 1 : -1);
  };

  // ---------- Klick in freie Zeit ----------
  // Auswahl-Modus: Urlaub/Krank der gewählten Mitarbeiter an diesem Tag? (dann nicht wählbar, mit Hinweis)
  const abwesendeGewaehlte = (sp) => {
    const wer = sp.monteur && sp.monteur !== OHNE ? [sp.monteur] : [...gewaehlt];
    return [...new Set(abwesenheiten.filter((a) => wer.includes(a.username) && abwesendAmTag(a, sp.tag)).map((a) => a.username))];
  };
  const sperreWegenUrlaub = (sp) => {
    const weg = abwesendeGewaehlte(sp);
    if (!weg.length) return false;
    toast.warning(`${weg.map(nameVon).join(", ")} ${weg.length > 1 ? "sind" : "ist"} an diesem Tag nicht da (Urlaub/Krank). Bitte einen anderen Tag wählen.`, { duration: 6000 });
    return true;
  };
  // Auswahl-Modus mit Maus: über freie Zeit ziehen = Start und Ende
  const [auswahlZug, setAuswahlZug] = useState(null);   // { key, a, b } in Minuten ab Mitternacht
  const auswahlRef = useRef(null);
  const minutenAn = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return bereich.von * 60 + ((e.clientY - r.top) / HOUR_H) * 60;
  };
  const zugStart = (e, sp) => {
    if (e.pointerType !== "mouse" || e.button !== 0 || e.target.closest("[data-karte],[data-markierung],[data-aufgabe]")) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* egal */ }
    auswahlRef.current = { y0: e.clientY, m0: minutenAn(e), aktiv: false };
  };
  const zugBewegen = (e, sp) => {
    const z = auswahlRef.current;
    if (!z) return;
    if (!z.aktiv && Math.abs(e.clientY - z.y0) < 6) return;
    z.aktiv = true;
    const m1 = minutenAn(e);
    setAuswahlZug({ key: sp.key, a: Math.min(z.m0, m1), b: Math.max(z.m0, m1) });
  };
  const zugEnde = (e, sp) => {
    const z = auswahlRef.current;
    auswahlRef.current = null;
    if (!z?.aktiv) return;
    const m1 = minutenAn(e);
    setAuswahlZug(null);
    klickSperre.current = true; setTimeout(() => { klickSperre.current = false; }, 400);
    let a = runden(Math.min(z.m0, m1)), b = runden(Math.max(z.m0, m1));
    if (b - a < 30) b = a + 30;
    if (sperreWegenUrlaub(sp)) return;
    const start = plusMinuten(tagAnfang(sp.tag), a);
    onNeu({ start: schreibeZeit(start), ende: schreibeZeit(plusMinuten(start, b - a)), monteur: sp.monteur && sp.monteur !== OHNE ? sp.monteur : "" });
  };
  const klickSpalte = (e, sp) => {
    if (e.target.closest("[data-karte]") || klickSperre.current) return;
    if (auswahl && sperreWegenUrlaub(sp)) return;
    const r = e.currentTarget.getBoundingClientRect();
    const min = bereich.von * 60 + ((e.clientY - r.top) / HOUR_H) * 60;
    const start = plusMinuten(tagAnfang(sp.tag), Math.floor(min / 30) * 30);
    onNeu({ start: schreibeZeit(start), ende: schreibeZeit(plusMinuten(start, 60)), monteur: sp.monteur && sp.monteur !== OHNE ? sp.monteur : "" });
  };
  const neuHeute = () => {
    onNeu(null);   // „+ Termin“-Knopf: Seitenleiste „Neuer Termin“ (Zeit dann im Kalender wählen oder „Nächste freie Zeit“)
  };

  // ---------- Bausteine ----------
  // Als Funktion (nicht als Bauteil) aufgerufen, damit die Karte beim Ziehen nicht neu aufgebaut wird (sonst geht der Griff verloren)
  const karte = (e, sp) => {
    const { t, z } = e;
    const monteure = monteureVon(t);
    const abgesagt = t.status === "abgesagt";
    const kon = konflikte.get(t.id);
    const beginn = Math.max(e.von, bereich.von * 60);
    const top = px(beginn - bereich.von * 60);
    const h = Math.max(px(e.bis - beginn), 22);
    const kurz = h < 40;
    const schmal = spaltenBreite / e.spuren < 105;   // wenige Pixel: nur Startzeit, keine Farbpunkte
    const links = (e.spur / e.spuren) * 100;
    const breite = 100 / e.spuren;
    const dashed = t.status === "wartet_auf_go";
    const wirdGezogen = vorschau?.id === t.id;
    const unter = [kundeKurz(t), t.ort].filter(Boolean).join(" · ");
    const zeit = `${hhmm(z.start)}–${hhmm(z.ende)}`;
    return (
      <div
        key={`${t.id}-${sp.key}`}
        data-karte
        data-testid={`kal-karte-${t.id}`}
        data-status={t.status}
        data-konflikt={kon ? "ja" : "nein"}
        role="button"
        tabIndex={0}
        title={`${t.titel}\n${zeit}${unter ? `\n${unter}` : ""}\n${STATUS_TEXT[t.status] || t.status}${monteure.length ? `\n${monteure.map(nameVon).join(", ")}` : ""}${kon ? `\nDoppelbelegung: ${konfliktText(kon, nameVon)}` : ""}`}
        onKeyDown={(ev) => { if (ev.key === "Enter") onOeffnen(t); }}
        onContextMenu={(ev) => { if (zieh.current) ev.preventDefault(); }}
        onPointerDown={(ev) => beginneZiehen(ev, t, z, sp.key.split("|")[0], sp.monteur, ev.target.closest("[data-griff]") ? "dehnen" : "ziehen-zeit")}
        onPointerMove={bewegen}
        onPointerUp={loslassen}
        onPointerCancel={abbrechen}
        onClick={(ev) => { ev.stopPropagation(); if (klickSperre.current) return; onOeffnen(t); }}
        lang="de" className="absolute rounded-[5px] cursor-pointer select-none shadow-sm text-foreground"
        style={{
          top, height: h, left: `calc(${links}% + 1px)`, width: `calc(${breite}% - 3px)`,
          background: `linear-gradient(${e.farbe}30, ${e.farbe}30), hsl(var(--background))`,
          borderLeft: `4px ${dashed ? "dashed" : "solid"} ${abgesagt ? GRAU : e.farbe}`,
          boxShadow: kon && !abgesagt ? "0 0 0 2px #dc2626" : undefined,
          opacity: wirdGezogen ? 0.35 : abgesagt ? 0.55 : 1,
          WebkitTouchCallout: "none", WebkitUserSelect: "none", zIndex: 2, overflow: "clip",
        }}
      >
        {/* Uhrzeit und Titel bleiben beim Scrollen oben im sichtbaren Bereich (lange Karten) */}
        <div className={`${schmal ? "px-1" : "px-1.5"} text-[11px] leading-[14px] ${kurz ? "py-[3px]" : "py-[2px]"}`} style={{ position: "sticky", top: kopfHoehe }}>
          {kurz ? (
            <div className="flex items-center gap-1">
              <span className={`truncate flex-1 ${abgesagt ? "line-through" : ""}`}><b>{hhmm(z.start)}</b> {t.titel}</span>
              <StatusZeichen status={t.status} konflikt={!!kon && !abgesagt} id={t.id} />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1">
                <b className="whitespace-nowrap">{schmal ? hhmm(z.start) : zeit}</b>
                <span className="ml-auto flex items-center gap-1 shrink-0">
                  {!schmal && monteure.length > 0 && (
                    <span className="flex -space-x-1">
                      {monteure.slice(0, 3).map((m) => (
                        <span key={m} className="w-3.5 h-3.5 rounded-full text-[7px] font-bold text-white flex items-center justify-center ring-1 ring-background" style={{ background: farbeVon(m) }}>{initialsOf(m)}</span>
                      ))}
                    </span>
                  )}
                  <StatusZeichen status={t.status} konflikt={!!kon && !abgesagt} id={t.id} />
                </span>
              </div>
              {schmal ? (
                <div className={`font-semibold truncate ${abgesagt ? "line-through" : ""}`}>{t.titel}</div>
              ) : (
                <div className={`font-semibold ${abgesagt ? "line-through" : ""}`} style={{ display: "-webkit-box", WebkitLineClamp: h >= 80 ? 3 : h >= 58 ? 2 : 1, WebkitBoxOrient: "vertical", overflow: "hidden", hyphens: "auto", WebkitHyphens: "auto", overflowWrap: "break-word" }}>{t.titel}</div>
              )}
              {h >= 58 && unter && <div className="truncate text-muted-foreground">{unter}</div>}
            </>
          )}
        </div>
        {e.endetHier && (
          <div data-griff className="absolute bottom-0 left-0 right-0 h-3.5 cursor-ns-resize flex items-end justify-center" style={{ touchAction: "none" }}>
            <span className="mb-[3px] block w-7 h-[3px] rounded bg-foreground/30" />
          </div>
        )}
      </div>
    );
  };

  const geist = (sp) => {
    if (!vorschau || vorschau.spalteKey !== sp.key) return null;
    const a = ausschnittAmTag({ start: vorschau.start, ende: vorschau.ende }, sp.tag);
    if (!a) return null;
    const t = termine.find((x) => x.id === vorschau.id);
    const farbe = farbeVon(sp.monteur && sp.monteur !== OHNE ? sp.monteur : vorschau.monteure[0]);
    const beginn = Math.max(a.von, bereich.von * 60);
    const k = findeKonflikte({ id: vorschau.id, start: vorschau.start, ende: vorschau.ende, monteure: vorschau.monteure }, eff);
    return (
      <div key={`geist-${sp.key}`} data-testid="kal-geist" className="absolute left-0.5 right-0.5 rounded-[5px] pointer-events-none text-[11px] leading-[14px] px-1.5 py-0.5 text-foreground"
        style={{ top: px(beginn - bereich.von * 60), height: Math.max(px(a.bis - beginn), 22), background: `${farbe}55`, border: `2px dashed ${k.length ? "#dc2626" : farbe}`, zIndex: 30 }}>
        <b>{hhmm(vorschau.start)}–{hhmm(vorschau.ende)}</b>
        <div className="truncate font-semibold">{t?.titel}</div>
        {k.length > 0 && <div className="text-red-700 dark:text-red-300 font-semibold">Doppelbelegung</div>}
      </div>
    );
  };

  const jetztMin = jetzt.getHours() * 60 + jetzt.getMinutes();
  const jetztSichtbar = jetztMin >= bereich.von * 60 && jetztMin <= bereich.bis * 60;
  const linienStil = {
    backgroundImage: "linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border) / 0.45) 1px, transparent 1px)",
    backgroundSize: `100% ${HOUR_H}px, 100% ${HOUR_H / 2}px`,
  };
  const stunden = Array.from({ length: bereich.bis - bereich.von }, (_, i) => bereich.von + i);

  // ---------- Zeitraster (Woche / Tag / je Monteur) ----------
  const zeitraster = () => {
    const n = spalten.length;
    const minBreite = ansicht === "monteure" ? 150 : mobil ? 0 : 118;
    return (
      <div ref={scroller} data-testid="kal-raster" className="overflow-auto relative"
        style={{ maxHeight: `calc(100vh - ${mobil ? rasterAbzug + 40 : rasterAbzug}px)`, minHeight: auswahl ? 260 : 380, touchAction: mobil ? "pan-y" : "auto" }}
        onPointerDown={wischStart} onPointerUp={wischEnde}>
        <div className="grid" style={{ gridTemplateColumns: `${mobil ? 38 : 50}px repeat(${n}, minmax(${minBreite}px, 1fr))`, minWidth: mobil ? 0 : undefined }}>
          {/* Kopfzeile */}
          <div className="sticky top-0 z-20 bg-background border-b" style={{ left: 0 }} />
          {spalten.map((sp) => {
            const heute = gleicherTag(sp.tag, jetzt);
            const abw = sp.monteur ? abwesenheiten.filter((a) => a.username === sp.monteur && abwesendAmTag(a, sp.tag)) : abwesendeAm(sp.tag);
            return (
              <div key={sp.key} data-kopf className="sticky top-0 z-20 bg-background border-b border-l px-1.5 py-1.5 text-center" data-testid={`kal-kopf-${sp.key}`}>
                {sp.monteur ? (
                  <div className="flex items-center justify-center gap-1.5 text-xs font-semibold">
                    {sp.monteur !== OHNE && <span className="w-5 h-5 rounded-full text-[9px] text-white flex items-center justify-center" style={{ background: farbeVon(sp.monteur) }}>{initialsOf(sp.monteur)}</span>}
                    <span className="truncate">{sp.monteur === OHNE ? "Ohne Monteur" : nameVon(sp.monteur)}</span>
                  </div>
                ) : (
                  <button type="button" onClick={() => { setDatum(sp.tag); waehleAnsicht("monteure"); }} disabled={mobil}
                    title={mobil ? undefined : "Diesen Tag je Monteur ansehen"} className="mx-auto flex items-center justify-center gap-0.5 sm:gap-1.5 text-[10px] sm:text-xs">
                    <span className="uppercase text-muted-foreground">{format(sp.tag, "EEEEEE", { locale: de })}</span>
                    <span className={`inline-flex items-center justify-center min-w-5 h-5 sm:min-w-6 sm:h-6 rounded-full text-xs sm:text-sm font-semibold ${heute ? "bg-primary text-primary-foreground" : ""}`}>{format(sp.tag, "d")}</span>
                  </button>
                )}
                {aufgabenAm(sp.tag, sp.monteur).length > 0 && (
                  <div className="mt-1 space-y-0.5" data-testid={`kal-aufgaben-${sp.key}`}>
                    {aufgabenAm(sp.tag, sp.monteur).slice(0, 3).map((a) => aufgabenStreifen(a, true))}
                    {aufgabenAm(sp.tag, sp.monteur).length > 3 && <div className="text-[10px] text-muted-foreground">+{aufgabenAm(sp.tag, sp.monteur).length - 3} Aufgaben</div>}
                  </div>
                )}
                {abw.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {abw.slice(0, 2).map((a, i) => (
                      <div key={i} className="rounded-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-[10px] leading-4 px-1 truncate" data-testid="kal-abwesend">
                        {sp.monteur ? (a.typ === "krank" ? "Krank" : "Urlaub") : `${a.typ === "krank" ? "Krank" : "Urlaub"}: ${a.name}`}
                      </div>
                    ))}
                    {abw.length > 2 && <div className="text-[10px] text-muted-foreground">+{abw.length - 2} weitere</div>}
                  </div>
                )}
              </div>
            );
          })}
          {/* Stundenspalte */}
          <div className="relative border-r" style={{ height: hoehe }}>
            {stunden.map((h, i) => (
              <div key={h} className="absolute right-1 text-[10px] text-muted-foreground -translate-y-1/2" style={{ top: i * HOUR_H }}>{i === 0 ? "" : `${String(h).padStart(2, "0")}:00`}</div>
            ))}
          </div>
          {spalten.map((sp) => {
            const heute = gleicherTag(sp.tag, jetzt);
            const abwSp = auswahl ? abwesendeGewaehlte(sp).length > 0 : (sp.monteur && sp.monteur !== OHNE && abwesenheiten.some((a) => a.username === sp.monteur && abwesendAmTag(a, sp.tag)));
            const mark = markierung && (sp.monteur === null || sp.monteur === OHNE ? true : (!gewaehlt.size || gewaehlt.has(sp.monteur))) ? ausschnittAmTag(markierung, sp.tag) : null;
            return (
              <div key={sp.key} data-spalte-tag={tagSchluessel(sp.tag)} data-spalte-key={sp.key} data-spalte-monteur={sp.monteur ?? undefined}
                data-testid={`kal-spalte-${sp.key}`}
                onClick={(e) => klickSpalte(e, sp)}
                onPointerDown={(e) => zugStart(e, sp)} onPointerMove={(e) => zugBewegen(e, sp)} onPointerUp={(e) => zugEnde(e, sp)}
                onPointerCancel={() => { auswahlRef.current = null; setAuswahlZug(null); }}
                className={`relative border-l cursor-cell ${heute ? "bg-primary/[0.04]" : ""}`} style={{ height: hoehe, ...linienStil }}>
                {abwSp && <div className="absolute inset-0 pointer-events-none opacity-60" style={{ background: "repeating-linear-gradient(45deg, rgba(148,163,184,0.25) 0 6px, transparent 6px 12px)" }} />}
                {(eintraegeJeSpalte[sp.key] || []).map((e) => karte(e, sp))}
                {geist(sp)}
                {mark && (
                  <div data-markierung data-testid="kal-vorschlag" role="button" tabIndex={0} title="Vorgeschlagene freie Zeit – anklicken zum Übernehmen"
                    onClick={(ev) => { ev.stopPropagation(); onNeu({ start: schreibeZeit(markierung.start), ende: schreibeZeit(markierung.ende), monteur: sp.monteur && sp.monteur !== OHNE ? sp.monteur : "" }); }}
                    className="absolute left-0.5 right-0.5 rounded-[5px] cursor-pointer text-[11px] leading-[14px] px-1 py-0.5 font-semibold text-emerald-900 dark:text-emerald-100 bg-emerald-400/40 border-2 border-emerald-600 overflow-hidden"
                    style={{ top: px(Math.max(mark.von, bereich.von * 60) - bereich.von * 60), height: Math.max(px(mark.bis - Math.max(mark.von, bereich.von * 60)), 22), zIndex: 25 }}>
                    Frei: {hhmm(markierung.start)}–{hhmm(markierung.ende)}
                  </div>
                )}
                {entwurf && (sp.monteur === null || sp.monteur === OHNE ? true : (!gewaehlt.size || gewaehlt.has(sp.monteur))) && (() => {
                  const e2 = ausschnittAmTag(entwurf, sp.tag);
                  if (!e2) return null;
                  const beginn2 = Math.max(e2.von, bereich.von * 60);
                  return (
                    <div data-testid="kal-entwurf" className="absolute left-0.5 right-0.5 rounded-[5px] pointer-events-none border-2 border-dashed border-emerald-600 bg-emerald-200/60 dark:bg-emerald-900/50 text-[11px] leading-[14px] px-1 py-0.5 font-semibold text-emerald-950 dark:text-emerald-50 overflow-hidden"
                      style={{ top: px(beginn2 - bereich.von * 60), height: Math.max(px(e2.bis - beginn2), 22), zIndex: 24 }}>
                      Neuer Termin{entwurf.titel ? <span className="block truncate font-normal">{entwurf.titel}</span> : null}
                    </div>
                  );
                })()}
                {auswahlZug?.key === sp.key && (
                  <div data-testid="kal-auswahl-zug" className="absolute left-0.5 right-0.5 rounded-[5px] pointer-events-none border-2 border-dashed border-primary bg-primary/20 text-[11px] leading-[14px] px-1 font-semibold"
                    style={{ top: px(runden(auswahlZug.a) - bereich.von * 60), height: Math.max(px(Math.max(runden(auswahlZug.b) - runden(auswahlZug.a), 30)), 22), zIndex: 30 }}>
                    {hhmm(plusMinuten(tagAnfang(sp.tag), runden(auswahlZug.a)))}–{hhmm(plusMinuten(tagAnfang(sp.tag), Math.max(runden(auswahlZug.b), runden(auswahlZug.a) + 30)))}
                  </div>
                )}
                {heute && jetztSichtbar && (
                  <div data-testid="kal-jetzt" className="absolute left-0 right-0 pointer-events-none" style={{ top: px(jetztMin - bereich.von * 60), zIndex: 10 }}>
                    <div className="h-[2px] bg-red-500" /><div className="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-red-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------- Monat ----------
  const monatsraster = () => {
    const wochentage = tage.slice(0, 7);
    return (
      <div data-testid="kal-monat" className="overflow-x-auto">
        <div className="grid grid-cols-7 border-b text-xs text-muted-foreground" style={{ minWidth: 700 }}>
          {wochentage.map((t) => <div key={t.getDay()} className="px-2 py-1.5 font-medium uppercase">{format(t, "EEEEEE", { locale: de })}</div>)}
        </div>
        <div className="grid grid-cols-7" style={{ minWidth: 700 }}>
          {tage.map((tag) => {
            const key = tagSchluessel(tag);
            const imMonat = tag.getMonth() === datum.getMonth();
            const heute = gleicherTag(tag, jetzt);
            const liste = eff.map((t) => ({ t, z: terminZeiten(t) })).filter((x) => x.z && ausschnittAmTag(x.z, tag))
              .sort((a, b) => a.z.start - b.z.start);
            const abw = abwesendeAm(tag);
            const ziel = vorschau?.spalteKey === `monat|${key}`;
            return (
              <div key={key} data-monat-tag={key} data-testid={`kal-tag-${key}`}
                onClick={(e) => { if (e.target.closest("[data-karte]") || klickSperre.current) return; onNeu({ start: `${key}T08:00`, ende: `${key}T09:00`, monteur: "" }); }}
                className={`border-b border-r min-h-[104px] p-1 cursor-cell ${imMonat ? "" : "bg-muted/40 text-muted-foreground"} ${ziel ? "ring-2 ring-primary ring-inset" : ""}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <button type="button" onClick={(e) => { e.stopPropagation(); setDatum(tag); waehleAnsicht("woche"); }} title="Woche öffnen"
                    className={`inline-flex items-center justify-center min-w-6 h-6 rounded-full text-xs font-semibold ${heute ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{tag.getDate()}</button>
                  {abw.length > 0 && <span className="text-[10px] rounded-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-1 truncate max-w-[70%]" data-testid="kal-abwesend">{abw.map((a) => a.name.split(" ")[0]).slice(0, 2).join(", ")}: {abw[0].typ === "krank" ? "Krank" : "Urlaub"}</span>}
                </div>
                <div className="space-y-0.5">
                  {aufgabenAm(tag).slice(0, 2).map((a) => aufgabenStreifen(a, true))}
                  {liste.slice(0, 3).map(({ t, z }) => {
                    const kon = konflikte.get(t.id);
                    const abg = t.status === "abgesagt";
                    const farbe = farbeVon(monteureVon(t)[0]);
                    return (
                      <div key={t.id} data-karte data-testid={`kal-karte-${t.id}`} data-konflikt={kon ? "ja" : "nein"} data-status={t.status} role="button" tabIndex={0}
                        title={`${t.titel}\n${hhmm(z.start)}–${hhmm(z.ende)}\n${STATUS_TEXT[t.status] || ""}`}
                        onPointerDown={(ev) => beginneZiehen(ev, t, z, key, null, "ziehen-tag")}
                        onPointerMove={bewegen} onPointerUp={loslassen} onPointerCancel={abbrechen}
                        onKeyDown={(ev) => { if (ev.key === "Enter") onOeffnen(t); }}
                        onClick={(ev) => { ev.stopPropagation(); if (klickSperre.current) return; onOeffnen(t); }}
                        className={`flex items-center gap-1 rounded-[4px] px-1 py-[1px] text-[11px] leading-4 cursor-pointer select-none text-foreground ${abg ? "line-through opacity-55" : ""} ${vorschau?.id === t.id ? "opacity-35" : ""}`}
                        style={{ background: `linear-gradient(${farbe}30, ${farbe}30), hsl(var(--background))`, borderLeft: `3px ${t.status === "wartet_auf_go" ? "dashed" : "solid"} ${abg ? GRAU : farbe}`, boxShadow: kon && !abg ? "0 0 0 1.5px #dc2626" : undefined }}>
                        <span className="font-semibold whitespace-nowrap">{hhmm(z.start)}</span>
                        <span className="truncate">{t.titel}</span>
                        {kon && !abg && <AlertTriangle className="w-3 h-3 text-red-600 shrink-0 ml-auto" />}
                      </div>
                    );
                  })}
                  {liste.length > 3 && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setDatum(tag); waehleAnsicht("monteure"); }}
                      className="text-[11px] text-primary hover:underline px-1" data-testid={`kal-mehr-${key}`}>+{liste.length - 3} weitere</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const ansichten = auswahl ? (mobil ? [["woche", "Woche"], ["tag", "Tag"]] : [["woche", "Woche"], ["tag", "Tag"], ["monteure", "Je Mitarbeiter"]]) : mobil ? [["tag", "Tag"]] : [["woche", "Woche"], ["monat", "Monat"], ["monteure", "Je Monteur"]];
  const anzahlWarnungen = [...konflikte.keys()].filter((id) => tage.some((tag) => { const t = eff.find((x) => x.id === id); const z = t && terminZeiten(t); return z && ausschnittAmTag(z, tag); })).length;

  return (
    <div data-testid="kalender" data-ansicht={ansicht}>
      {/* Werkzeugleiste */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => weiter(-1)} className="h-9 w-9 flex items-center justify-center border rounded-sm hover:bg-muted" title="Zurück" aria-label="Zurück" data-testid="kal-zurueck"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" onClick={() => setDatum(tagAnfang(new Date()))} className="h-9 px-3 border rounded-sm hover:bg-muted text-sm font-medium" data-testid="kal-heute">Heute</button>
          <button type="button" onClick={() => weiter(1)} className="h-9 w-9 flex items-center justify-center border rounded-sm hover:bg-muted" title="Weiter" aria-label="Weiter" data-testid="kal-vor"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <h2 className="text-sm sm:text-lg font-semibold flex-1 min-w-0 truncate" data-testid="kal-titel">{titelText}</h2>
        {laden && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground shrink-0" />}
        {ansichten.length > 1 && (
          <div className="inline-flex border rounded-sm overflow-hidden shrink-0" role="group" aria-label="Ansicht">
            {ansichten.map(([k, l]) => (
              <button key={k} type="button" onClick={() => waehleAnsicht(k)} data-testid={`kal-ansicht-${k}`}
                className={`h-9 px-3 text-sm ${ansicht === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{l}</button>
            ))}
          </div>
        )}
        <button type="button" onClick={neuHeute} className="h-9 w-9 sm:w-auto sm:px-3 shrink-0 flex items-center justify-center gap-1 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90" title="Neuer Termin" aria-label="Neuer Termin" data-testid="kal-neu">
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Termin</span>
        </button>
      </div>

      {/* Legende: Monteure (Klick = nur diese zeigen) und Zeichen */}
      {auswahl ? (
        <div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-xs text-muted-foreground" data-testid="kal-legende">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: AUFGABE_FARBE }} />Aufgabe</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border" style={{ background: "repeating-linear-gradient(45deg, rgba(148,163,184,0.5) 0 3px, transparent 3px 6px)" }} />Urlaub/Krank (nicht wählbar)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-emerald-600 bg-emerald-400/40" />freie Zeit (Vorschlag)</span>
        </div>
      ) : (
      <div className="flex items-center gap-x-3 gap-y-1.5 mb-3 text-xs flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible" data-testid="kal-legende">
        {!auswahl && <div className="inline-flex border rounded-sm overflow-hidden shrink-0" role="group" aria-label="Anzeigen" data-testid="kal-zeige">
          {[["termine", "Termine"], ["aufgaben", "Aufgaben"], ["beides", "Beides"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => waehleZeige(k)} data-testid={`kal-zeige-${k}`} aria-pressed={zeige === k}
              className={`h-9 px-2 sm:px-3 text-xs sm:text-sm ${zeige === k ? (k === "aufgaben" ? "bg-violet-600 text-white" : "bg-primary text-primary-foreground") : "hover:bg-muted"}`}>{l}</button>
          ))}
        </div>}
        {legende.map((u) => {
          const an = gewaehlt.has(u);
          return (
            <button key={u} type="button" onClick={() => onMonteurUmschalten?.(u)} data-testid={`kal-monteur-${u}`}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border whitespace-nowrap shrink-0 ${an ? "bg-foreground text-background border-transparent" : gewaehlt.size ? "opacity-60 hover:opacity-100" : "hover:bg-muted"}`}>
              <span className="w-3 h-3 rounded-full" style={{ background: farbeVon(u) }} />{nameVon(u)}
            </button>
          );
        })}
        <span className="text-muted-foreground hidden sm:flex items-center gap-4 ml-auto flex-wrap">
          <span className="flex items-center gap-1.5"><span className="text-[9px] leading-3 font-bold px-1 rounded-sm bg-amber-500 text-white">GO?</span>wartet auf GO</span>
          <span className="flex items-center gap-1.5"><span className="line-through opacity-60">Termin</span>abgesagt</span>
          <span className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-red-600" />Doppelbelegung</span>
        </span>
      </div>
      )}

      {anzahlWarnungen > 0 && !auswahl && (
        <div className="mb-3 flex items-center gap-2 rounded-sm border border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-900 text-red-800 dark:text-red-200 text-xs sm:text-sm px-3 py-1.5 sm:py-2" data-testid="kal-warnung">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{anzahlWarnungen === 1 ? "Ein Termin" : `${anzahlWarnungen} Termine`} in dieser Ansicht {anzahlWarnungen === 1 ? "überschneidet" : "überschneiden"} sich beim selben Monteur (rot).</span>
        </div>
      )}

      <div className="border rounded-md bg-background overflow-hidden">
        {ansicht === "monat" ? monatsraster() : zeitraster()}
      </div>
    </div>
  );
}

function StatusZeichen({ status, konflikt, id }) {
  return (
    <span className="flex items-center gap-0.5 shrink-0" data-testid={`kal-status-${id}`}>
      {konflikt && <AlertTriangle className="w-3.5 h-3.5 text-red-600" data-testid={`kal-warnung-${id}`} />}
      {status === "wartet_auf_go" && <span className="text-[9px] leading-3 font-bold px-1 rounded-sm bg-amber-500 text-white">GO?</span>}
      {status === "bestaetigt" && <Clock className="w-3 h-3 text-blue-600" />}
      {(status === "im_kalender" || status === "erledigt") && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
      {status === "abgesagt" && <XCircle className="w-3 h-3 text-red-600" />}
    </span>
  );
}
